/* eslint max-len: ["error", { "code": 700 }] */
import React, { useContext } from 'react';
import PricingContext from '../../../context/pricing';
import { startFree } from '../../../utils/pricing';
import { SIGNUP_URL } from '../../../constants/pricing/config';

import Question from '../../Question';

const FrequentlyAsked = () => {
  const { state } = useContext(PricingContext);
  const { activeTab } = state;

  return (
    <div className="m-bottom-10">
      <div className="row row--large text-center">
        <h5>Ask Us</h5>
        <h2>
          {activeTab === 'mc' ? 'Marketing Campaigns' : 'Email API'} Frequently
          Asked Questions
        </h2>
      </div>
      <div className="row row--large m-top-5">
        <dl>
          <Question title="How does Twilio SendGrid work?">
            {activeTab === 'mc' ? (
              <>
                <p>
                  Twilio SendGrid is a leader in email deliverability that
                  empowers developers and marketers to drive meaningful and
                  lasting engagements with their customers. Marketing Campaigns
                  is our multichannel service that’s designed for marketers with
                  streamlined and flexible campaign creation, plus
                  easy-to-implement APIs for developers.
                </p>
                <p>
                  In Marketing Campaigns, you can create, send, and track
                  one-to-many messages as well as automated emails. Because it’s
                  backed by our cloud-based SMTP provider, which acts as an
                  email delivery engine, you can send email without the cost and
                  complexity of maintaining your own email servers. It takes the
                  complication out of email creation so you can quickly and
                  easily produce professional emails to achieve your business
                  goals.
                </p>
                <p>
                  Learn more:{' '}
                  <a href="https://sendgrid.com/docs/for-developers/sending-email/api-getting-started/">
                    How to Send Email With Marketing Campaigns
                  </a>
                </p>
              </>
            ) : (
              <>
                <p>
                  Twilio SendGrid is a cloud-based SMTP provider that acts as an
                  email delivery engine, allowing you to send email without the
                  cost and complexity of maintaining your own email servers.
                  Twilio SendGrid manages the technical details of email
                  delivery, like infrastructure scaling, ISP outreach,
                  reputation monitoring, and real-time analytics.
                </p>
                <p>
                  Learn more:{' '}
                  <a href="https://sendgrid.com/docs/for-developers/sending-email/api-getting-started/">
                    Getting Started with Email API
                  </a>
                </p>
              </>
            )}
          </Question>

          <Question title="How does the free trial work?">
            {activeTab === 'mc' ? (
              <>
                <p>
                  Once you create and verify your account, you can send up to
                  6,000 emails per month and store up to 2,000 contacts for
                  free, using Marketing Campaigns.
                </p>
                <p>
                  Ready to get started?{' '}
                  <a href={SIGNUP_URL} onClick={startFree}>
                    Sign Up for My Free Account
                  </a>
                </p>
              </>
            ) : (
              <>
                <p>
                  Once you create a{' '}
                  <a href="https://sendgrid.com/docs/for-developers/sending-email/sender-identity/">
                    sender identity
                  </a>{' '}
                  a, you can send 100 emails per day. If you need to send more
                  than that,{' '}
                  <a href="https://sendgrid.com/docs/ui/account-and-settings/verifying-your-account/">
                    complete your account setup
                  </a>{' '}
                  to upgrade to a paid plan!
                </p>
                <p>
                  <a href={SIGNUP_URL} onClick={startFree}>
                    Sign Up for My Free Account
                  </a>
                </p>
              </>
            )}
          </Question>

          {activeTab === 'mc' ? (
            <Question title="Do I have to sign up for the Email API and Marketing Campaigns separately?">
              <p>
                Yes! If you want to use the Email API in conjunction with your
                Marketing Campaigns account, you will need to purchase the plans
                separately. But once you sign up, you can use the same account
                to send via the Email API or through Marketing Campaigns.
              </p>
              <p>
                Learn more:{' '}
                <a href="https://sendgrid.com/docs/User_Guide/Marketing_Campaigns/index.html">
                  How to Send Email With Marketing Campaigns
                </a>
              </p>
            </Question>
          ) : (
            <Question title="Do I have to sign up for the Marketing Campaigns and Email API separately?">
              <p>
                Yes! If you want to use Marketing Campaigns and the Email API,
                you will need to purchase the plans separately. But once you
                sign up, you can use the same account to send via the Email API
                or through Marketing Campaigns.
              </p>
              <p>
                Learn more:{' '}
                <a href="https://sendgrid.com/docs/User_Guide/Marketing_Campaigns/index.html">
                  How to Send Email With Marketing Campaigns
                </a>
              </p>
            </Question>
          )}

          <Question title="How do you measure the number of emails I send?">
            {activeTab === 'mc' ? (
              <>
                <p>
                  Every time you send one email to one recipient, it’s deducted
                  as one email credit from your Marketing Campaigns plan. This
                  occurs whether the email is ultimately deferred or sent to
                  spam by the inbox provider.
                </p>
                <p>
                  It’s important to keep in mind that emails sent using an Email
                  API package are counted separately from emails that you send
                  using a Marketing Campaigns package.{' '}
                </p>
              </>
            ) : (
              <>
                <p>
                  Every time you send one email to one recipient, it’s deducted
                  as one email credit from your Email API plan. This occurs
                  whether the email is ultimately deferred or sent to spam by
                  the inbox provider.
                </p>
                <p>
                  Also note that emails you send via Marketing Campaigns will be
                  counted separately from any emails you send via the Email API.
                </p>
              </>
            )}
            <p>
              Learn more about the billing structure:{' '}
              <a href="https://sendgrid.com/docs/ui/account-and-settings/billing/">
                Billing Info and FAQs
              </a>
            </p>
          </Question>

          <Question title="What happens if I go over my plan?">
            <p>
              If you send more emails than your plan allows within a given
              billing period, you will be charged a small overage fee on a
              per-email basis. Any overage fees are added to your invoice for
              the following billing period.
            </p>
            <p>
              Learn more about overage costs:{' '}
              <a href="https://sendgrid.com/docs/ui/account-and-settings/billing/#overages">
                Overages
              </a>
            </p>
          </Question>

          <Question title="Can you help me get started? Do you offer deliverability consulting?">
            {activeTab === 'mc' ? (
              <p>
                Yes! Our Onboarding Specialists can walk you through the entire
                set-up process to ensure that your account is set-up for
                success. You can also work with our Delivery Consultants to
                evaluate your entire email program and get a detailed assessment
                to help you identify areas for optimization, such as configuring
                your IPs correctly or diagnosing email deliverability issues.
              </p>
            ) : (
              <p>
                Yes! Our Onboarding Specialists can walk you through the entire
                set-up process to ensure that your email environment is built
                for success. You can also work with our Delivery Consultants to
                evaluate your entire email program and get a detailed assessment
                to help you identify areas for optimization, such as configuring
                your IPs correctly or diagnosing email deliverability issues.
              </p>
            )}
            <p>
              Interested? Learn more:{' '}
              <a href="https://sendgrid.com/marketing/delivery-consult/">
                Twilio SendGrid’s Expert Services
              </a>
            </p>
          </Question>

          <Question title="Why do I need to verify my account?">
            <p>
              We require all of our customers to verify their accounts. This
              helps to safeguard your account and maintain deliverability for
              you and all of our customers.
            </p>
            <p>
              Learn more:{' '}
              <a href="https://sendgrid.com/docs/ui/account-and-settings/verifying-your-account/">
                Verifying Your Account
              </a>
            </p>
          </Question>
        </dl>
      </div>
    </div>
  );
};

export default FrequentlyAsked;
